import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Gift, Users, Share2, Award, CheckCircle, Star, Mail, Calendar, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const FreeCredits = () => {
  const navigate = useNavigate();
  const [referralEmail, setReferralEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    if (!referralEmail) return;
    
    setIsSubmitting(true);
    // Simulate referral submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Referral sent! You\'ll earn credits when they sign up.');
    setReferralEmail('');
    setIsSubmitting(false);
  };

  const freeCreditsPrograms = [
    {
      title: 'New User Welcome',
      icon: Gift,
      credits: 5,
      description: 'Get 5 free credits when you create your account',
      requirements: [
        'Create a new Tax Helper Pros account',
        'Verify your email address',
        'Complete your profile'
      ],
      status: 'active',
      color: 'green'
    },
    {
      title: 'Referral Program',
      icon: Share2,
      credits: 10,
      description: 'Earn 10 credits for each friend who signs up',
      requirements: [
        'Friend creates account using your referral link',
        'They process their first document',
        'Credits added to your account within 24 hours'
      ],
      status: 'active',
      color: 'blue'
    },
    {
      title: 'Social Media Sharing',
      icon: Star,
      credits: 3,
      description: 'Share your experience and earn credits',
      requirements: [
        'Share Tax Helper Pros on LinkedIn, Twitter, or Facebook',
        'Tag @TaxHelperPro and use #TaxHelperPro',
        'Submit link for verification'
      ],
      status: 'active',
      color: 'purple'
    },
    {
      title: 'Review & Testimonial',
      icon: Award,
      credits: 15,
      description: 'Help others by sharing your experience',
      requirements: [
        'Process at least 3 documents',
        'Write a detailed review (100+ words)',
        'Submit via our testimonial form'
      ],
      status: 'active',
      color: 'orange'
    },
    {
      title: 'Beta Feature Testing',
      icon: Zap,
      credits: 20,
      description: 'Test new features and provide feedback',
      requirements: [
        'Join our beta testing program',
        'Test assigned features for 1 week',
        'Provide detailed feedback report'
      ],
      status: 'limited',
      color: 'red'
    },
    {
      title: 'Holiday Promotions',
      icon: Calendar,
      credits: 25,
      description: 'Special seasonal credit bonuses',
      requirements: [
        'Available during tax season and holidays',
        'Sign up during promotional periods',
        'Complete required promotional actions'
      ],
      status: 'seasonal',
      color: 'pink'
    }
  ];

  const programsByStatus = {
    active: freeCreditsPrograms.filter(p => p.status === 'active'),
    limited: freeCreditsPrograms.filter(p => p.status === 'limited'),
    seasonal: freeCreditsPrograms.filter(p => p.status === 'seasonal')
  };

  const totalPossibleCredits = freeCreditsPrograms
    .filter(p => p.status === 'active')
    .reduce((sum, program) => sum + program.credits, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Gift className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Free Credits Program
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Earn free processing credits through our community programs and promotions.
          </p>
        </div>

        {/* Credits Overview */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50 mb-12">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-green-800 mb-4">Earn Up to {totalPossibleCredits} Free Credits</h2>
              <p className="text-green-700 text-lg">Multiple ways to earn free processing credits and save money on document analysis.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">5 Credits</h3>
                <p className="text-slate-600">Welcome Bonus</p>
                <p className="text-sm text-slate-500 mt-2">Just for signing up</p>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">10 Credits</h3>
                <p className="text-slate-600">Per Referral</p>
                <p className="text-sm text-slate-500 mt-2">Unlimited referrals</p>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">15+ Credits</h3>
                <p className="text-slate-600">Community Actions</p>
                <p className="text-sm text-slate-500 mt-2">Reviews, sharing, testing</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Programs */}
        <Card className="shadow-lg border-0 mb-8">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="text-blue-800">Active Credit Programs</CardTitle>
            <p className="text-blue-700">Available now - start earning immediately!</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {programsByStatus.active.map((program, index) => {
                const IconComponent = program.icon;
                return (
                  <div key={index} className={`bg-${program.color}-50 border border-${program.color}-200 rounded-lg p-6`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 bg-${program.color}-100 rounded-lg flex items-center justify-center`}>
                          <IconComponent className={`w-6 h-6 text-${program.color}-600`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{program.title}</h4>
                          <p className={`text-${program.color}-600 font-medium`}>{program.credits} Credits</p>
                        </div>
                      </div>
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">Active</span>
                    </div>
                    <p className="text-slate-700 mb-4">{program.description}</p>
                    <div className="space-y-2">
                      <h5 className="font-medium text-slate-800 text-sm">Requirements:</h5>
                      {program.requirements.map((req, reqIndex) => (
                        <div key={reqIndex} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600 text-sm">{req}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Referral Program Spotlight */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mb-8">
          <CardHeader className="bg-blue-100 border-b">
            <CardTitle className="flex items-center gap-3 text-blue-800">
              <Share2 className="w-6 h-6" />
              Referral Program - Earn Unlimited Credits
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-blue-800 mb-4">How It Works</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Share your unique referral link</p>
                      <p className="text-slate-600 text-sm">Available in your dashboard once you sign up</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                    <div>
                      <p className="font-medium text-slate-800">Friend signs up and processes first document</p>
                      <p className="text-slate-600 text-sm">They get welcome credits, you get referral credits</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                    <div>
                      <p className="font-medium text-slate-800">10 credits added to your account</p>
                      <p className="text-slate-600 text-sm">Credits appear within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800 mb-4">Quick Referral</h4>
                <p className="text-blue-700 mb-4">Send a referral invitation to a friend right now:</p>
                <form onSubmit={handleReferralSubmit} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="Friend's email address"
                      value={referralEmail}
                      onChange={(e) => setReferralEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Referral
                      </>
                    )}
                  </Button>
                </form>
                <p className="text-blue-600 text-sm mt-3">
                  * Requires Tax Helper Pros account to send referrals
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limited & Seasonal Programs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Limited Programs */}
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-orange-50 border-b">
              <CardTitle className="text-orange-800">Limited Availability</CardTitle>
              <p className="text-orange-700">Special opportunities with limited spots</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {programsByStatus.limited.map((program, index) => {
                  const IconComponent = program.icon;
                  return (
                    <div key={index} className="border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-${program.color}-100 rounded-lg flex items-center justify-center`}>
                            <IconComponent className={`w-5 h-5 text-${program.color}-600`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{program.title}</h4>
                            <p className={`text-${program.color}-600 font-medium text-sm`}>{program.credits} Credits</p>
                          </div>
                        </div>
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium">Limited</span>
                      </div>
                      <p className="text-slate-700 text-sm">{program.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Seasonal Programs */}
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-pink-50 border-b">
              <CardTitle className="text-pink-800">Seasonal Promotions</CardTitle>
              <p className="text-pink-700">Holiday and tax season specials</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {programsByStatus.seasonal.map((program, index) => {
                  const IconComponent = program.icon;
                  return (
                    <div key={index} className="border border-pink-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-${program.color}-100 rounded-lg flex items-center justify-center`}>
                            <IconComponent className={`w-5 h-5 text-${program.color}-600`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{program.title}</h4>
                            <p className={`text-${program.color}-600 font-medium text-sm`}>{program.credits} Credits</p>
                          </div>
                        </div>
                        <span className="bg-pink-100 text-pink-800 text-xs px-2 py-1 rounded-full font-medium">Seasonal</span>
                      </div>
                      <p className="text-slate-700 text-sm">{program.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Terms & Conditions */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-slate-800">Free Credits Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="prose prose-slate max-w-none text-sm">
              <ul className="space-y-2 text-slate-700">
                <li>• Free credits have no cash value and cannot be transferred or refunded</li>
                <li>• Credits never expire once added to your account</li>
                <li>• Free credits are applied before paid credits or pay-per-use charges</li>
                <li>• Referral credits are awarded only after the referred user processes their first document</li>
                <li>• Social media and review credits require manual verification (allow 2-3 business days)</li>
                <li>• Beta testing programs have limited availability and specific requirements</li>
                <li>• Tax Helper Pros reserves the right to modify or discontinue credit programs at any time</li>
                <li>• Abuse of the credit system may result in account suspension</li>
                <li>• Seasonal promotions are time-limited and subject to specific terms</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Get Started CTA */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Gift className="w-8 h-8 text-green-600" />
              <h3 className="text-2xl font-bold text-green-800">Start Earning Free Credits Today</h3>
            </div>
            <p className="text-green-700 text-lg mb-6">
              Create your account now and get 5 welcome credits instantly. Then refer friends, 
              share your experience, and earn even more!
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              >
                Get Started & Earn 5 Credits
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/help')}
                className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3"
              >
                Learn More
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/pay-per-use')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View Pay-Per-Use Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FreeCredits;